/**
 * Renders a ResolvedDocument.
 *
 * This walks blocks and looks each one up in the catalog. It has no knowledge of any
 * template, format or client — constraint C2. If this file ever needs a conditional on
 * a template id, the design has failed.
 */

import * as React from 'react';
import type { ResolvedBlock, ResolvedDocument, ResolvedPage } from '@pdmg/reporting-bindings';
import {
  BLOCK_COMPONENTS, AssetContext, SourceContext,
  type AssetResolver, type BlockProps,
} from './blocks.js';
import { IconStyleContext } from './icons.js';
import { themeToCssVars, PRINT_CSS } from './theme.js';

function Block({ block, top = false }: { block: ResolvedBlock; top?: boolean }) {
  const Component = BLOCK_COMPONENTS[block.type];
  if (!Component) {
    // Publish-time validation rejects unknown types and the resolver throws on them,
    // so reaching here means the registry and the catalog have diverged. Say which.
    throw new Error(
      `No component registered for block type "${block.type}". ` +
      `The manifest exists in the registry but nothing renders it.`
    );
  }
  const props: BlockProps = {
    props: block.props,
    values: block.values,
    manifest: block.manifest,
    children: block.children.map((c) => <Block key={c.id} block={c} />),
  };
  /*
   * Provenance is provided here rather than passed as a prop so that `<V>` can read it
   * from anywhere inside a block, however deeply the component nests its own markup.
   * On the print path `EditContext` is null and `<V>` ignores this entirely.
   */
  const rendered = (
    <SourceContext.Provider value={block.sources}>
      <Component {...props} />
    </SourceContext.Provider>
  );
  if (!top) return rendered;
  // Top-level blocks carry the attributes the paginator measures and splits on. A
  // wrapper rather than props on the component itself, so every block in the catalog
  // is paginatable without each one having to remember to spread them.
  return (
    <div
      data-block-id={block.id}
      data-flow={block.manifest.flow}
      data-repeat-header={String(block.manifest.repeatHeaderOnBreak)}
    >
      {rendered}
    </div>
  );
}

function Page(
  { page, header, footer, notice, pageNumber, totalPages }: {
    page: ResolvedPage; header: ResolvedBlock[]; footer: ResolvedBlock[];
    notice?: string; pageNumber?: number; totalPages?: number;
  }
) {
  return (
    <section className="page" data-page-id={page.id} data-mode={page.mode}>
      {/* Chrome is engine-placed on every page, never authored into the flow (C4). */}
      {header.map((b) => <Block key={`${page.id}-${b.id}`} block={b} />)}
      <div className="page-body stack">
        {page.blocks.map((b) => <Block key={b.id} block={b} top />)}
      </div>
      <div className="page-foot">
        {footer.map((b) => <Block key={`${page.id}-${b.id}`} block={b} />)}
        {/*
          * The copyright + page number, in-flow, for the ON-SCREEN preview only. In the PDF this
          * whole line lives in Chromium's margin box (only it knows the paginated page count), so
          * the render worker leaves `notice` unset and this renders nothing — no duplication. The
          * preview has no margin box, so without this the copyright would be invisible on screen.
          */}
        {notice ? (
          <div className="page-notice">
            {notice}
            <span className="sep">&#8226;</span>
            <span className="page-num">Page {pageNumber} of {totalPages}</span>
          </div>
        ) : null}
      </div>
    </section>
  );
}

/**
 * The copyright line, assembled from the same values the `PageFooter` chrome block binds, so the
 * preview cannot drift from what the template says. Mirrors the render worker's margin-box notice.
 */
function copyrightNotice(doc: ResolvedDocument): string {
  const block = doc.footer.find((b) => b.type === 'PageFooter');
  const v = (block?.values ?? {}) as { year?: unknown; companyName?: unknown };
  const year = v.year === undefined || v.year === null || v.year === '' ? new Date().getFullYear() : v.year;
  const company = typeof v.companyName === 'string' && v.companyName ? v.companyName : 'Your Company Name';
  return `© ${year}, ${company} All Rights Reserved.`;
}

export interface DocumentProps {
  doc: ResolvedDocument;
  resolveAsset?: AssetResolver;
  /** Extra CSS injected after the print stylesheet — embedded @font-face rules. */
  extraCss?: string;
  /**
   * Render the copyright + page number in-flow at the foot of each page. For the ON-SCREEN preview
   * only — the PDF gets this from Chromium's margin box, so the render worker leaves it off.
   */
  showFooterNotice?: boolean;
}

/*
 * The return type names `React` on purpose.
 *
 * `import * as React` looks unused under the automatic JSX runtime, and a consumer
 * compiling this source with `noUnusedLocals` — the web app does — will say so. It is
 * not unused: the render worker runs this file through esbuild with the CLASSIC
 * transform, which emits `React.createElement`, so deleting the import turns every PDF
 * render into "ReferenceError: React is not defined". That happened. Referencing the
 * namespace in a signature keeps both compilers honest.
 */
export function ReportDocument(
  { doc, resolveAsset = () => null, extraCss = '', showFooterNotice = false }: DocumentProps
): React.ReactElement {
  const notice = showFooterNotice ? copyrightNotice(doc) : undefined;
  const totalPages = doc.pages.length;
  return (
    <AssetContext.Provider value={resolveAsset}>
      <IconStyleContext.Provider value={(doc.theme as { iconStyle?: 'legacy' | 'v2' }).iconStyle ?? 'legacy'}>
        <style dangerouslySetInnerHTML={{ __html: `${themeToCssVars(doc.theme)}\n${PRINT_CSS}\n${extraCss}` }} />
        {doc.pages.map((page, i) => (
          <Page key={page.id} page={page} header={page.header ?? doc.header} footer={doc.footer}
            notice={notice} pageNumber={i + 1} totalPages={totalPages} />
        ))}
      </IconStyleContext.Provider>
    </AssetContext.Provider>
  );
}
